import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLaunchById, getRocketById } from '../services/dataService';
import CountdownTimer from '../components/CountdownTimer';
import ProjectionAnimation from '../components/ProjectionAnimation';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * Launch Details Page Component
 * Shows detailed launch information with countdown and projection
 */
const LaunchDetails = () => {
  const { id } = useParams();
  const launch = getLaunchById(id);
  const rocket = launch ? getRocketById(launch.rocketId) : null;
  const { canAccessPremium } = useSubscription();
  const isUpcoming = launch && new Date(launch.launchDate) > new Date();

  if (!launch) {
    return (
      <div className="container" style={{ paddingTop: '50px', textAlign: 'center' }}>
        <h2>Launch not found</h2>
        <Link to="/launches" className="btn btn-primary" style={{ marginTop: '20px' }}>Back to Launches</Link>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '30px' }}>
      <Link to="/launches" className="btn btn-secondary" style={{ marginBottom: '20px' }}>
        ← Back to Launches
      </Link>

      <div className="card" style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '36px', color: 'var(--highlight-color)', marginBottom: '20px' }}>
          {launch.missionName}
        </h1>

        {/* Countdown Timer for Upcoming Launches */}
        {isUpcoming && (
          <div style={{ marginBottom: '30px', padding: '20px', background: 'var(--card-hover)', borderRadius: '10px' }}>
            <h2 style={{ textAlign: 'center', marginBottom: '20px', fontSize: '28px' }}>
              ⏱️ Countdown to Launch
            </h2>
            <CountdownTimer targetDate={launch.launchDate} />
          </div>
        )}

        {/* Mission Information */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <h3 style={{ marginBottom: '15px', color: 'var(--text-dark)' }}>Launch Information</h3>
            <p style={{ marginBottom: '10px' }}><strong>Date & Time:</strong> {new Date(launch.launchDate).toLocaleString()}</p>
            <p style={{ marginBottom: '10px' }}><strong>Launch Site:</strong> {launch.launchSite}</p>
            <p style={{ marginBottom: '10px' }}><strong>Status:</strong> {launch.status}</p>
            <p style={{ marginBottom: '10px' }}><strong>Mission Type:</strong> {launch.missionType}</p>
          </div>

          <div>
            <h3 style={{ marginBottom: '15px', color: 'var(--text-dark)' }}>Rocket Information</h3>
            {rocket ? (
              <>
                <p style={{ marginBottom: '10px' }}><strong>Rocket:</strong> {rocket.name}</p>
                <p style={{ marginBottom: '10px' }}><strong>Type:</strong> {rocket.type}</p>
                <p style={{ marginBottom: '10px' }}><strong>Manufacturer:</strong> {rocket.manufacturer}</p>
                <Link to={`/rockets/${rocket.id}`} className="btn btn-secondary" style={{ marginTop: '10px', display: 'inline-block' }}>
                  View Rocket Details
                </Link>
              </>
            ) : (
              <p>Rocket information not available</p>
            )}
          </div>

          <div>
            <h3 style={{ marginBottom: '15px', color: 'var(--text-dark)' }}>Payload</h3>
            <p style={{ marginBottom: '10px' }}>{launch.payloadDetails}</p>
          </div>
        </div>
      </div>

      {/* Mission Timeline */}
      <section className="card" style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>📅 Mission Timeline</h2>
        <div style={{ paddingLeft: '20px', borderLeft: '3px solid var(--highlight-color)' }}>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--highlight-color)' }}>T-0: Launch</h3>
            <p style={{ color: 'var(--text-dark)' }}>{new Date(launch.launchDate).toLocaleString()}</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--highlight-color)' }}>T+1: Stage Separation</h3>
            <p style={{ color: 'var(--text-dark)' }}>First stage separation expected</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--highlight-color)' }}>T+5: Orbital Insertion</h3>
            <p style={{ color: 'var(--text-dark)' }}>Payload deployment to target orbit</p>
          </div>
          {launch.status === 'Completed' && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--highlight-color)' }}>✓ Mission Success</h3>
              <p style={{ color: 'var(--text-dark)' }}>Mission completed successfully</p>
            </div>
          )}
        </div>
      </section>

      {/* Launch Projection (Premium Feature) */}
      {isUpcoming && canAccessPremium() && (
        <section style={{ marginBottom: '30px' }}>
          <ProjectionAnimation launchDate={launch.launchDate} />
        </section>
      )}

      {isUpcoming && !canAccessPremium() && (
        <section className="card" style={{ marginBottom: '30px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>🚀 Launch Projection</h2>
          <p style={{ marginBottom: '20px', color: 'var(--text-dark)' }}>
            Premium subscribers can access detailed launch trajectory projections and advanced mission data.
          </p>
          <Link to="/subscription" className="btn btn-primary">
            Subscribe Now
          </Link>
        </section>
      )}
    </div>
  );
};

export default LaunchDetails;
